// src/pages/fiscalization/FiscalizationSettingsPage.tsx
// Admin config, tabbed: fiscal providers and fiscal policies — setup-time
// tasks, grouped like calendar's settings tabs (docs/ux/calendar-ia.md).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { cn } from '@core/ui';
import { ProvidersTab } from '@/features/fiscalization/ProvidersTab';
import { PoliciesTab } from '@/features/fiscalization/PoliciesTab';

type Tab = 'providers' | 'policies';

export const FiscalizationSettingsPage = observer(() => {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('providers');

  const tabs: { id: Tab; label: string }[] = [
    { id: 'providers', label: t('fiscalization.providers.title') },
    { id: 'policies', label: t('fiscalization.policies.title') },
  ];

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-2">
          <Link to="/fiscalization" className="text-muted-foreground hover:text-foreground">{t('fiscalization.dashboard.title')}</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('fiscalization.settings.title')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('fiscalization.settings.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('fiscalization.settings.subtitle')}</p>
      </div>

      <div className="flex items-center gap-1 border-b border-border" role="tablist">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            type="button"
            role="tab"
            aria-selected={tab === tb.id}
            onClick={() => setTab(tb.id)}
            className={cn(
              'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === tb.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tb.label}
          </button>
        ))}
      </div>

      <div>
        {tab === 'providers' && <ProvidersTab />}
        {tab === 'policies' && <PoliciesTab />}
      </div>
    </div>
  );
});

export default FiscalizationSettingsPage;
